import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { ScreenService } from '@catch/services';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { TranslocoPipe } from '@jsverse/transloco';
import { Store } from '@ngrx/store';
import { AppState } from '../../app.reducer';
import { Job } from '../../interfaces/job';
import { CampaignBookingModalComponent } from '../../modules/campaigns/components/campaign-booking-modal/campaign-booking-modal.component';
import { BreadcrumbType } from '../../routing/view-header.types';
import { JobsService } from '../../services/company/jobs.service';
import { FontawesomeService } from '../../services/fontawesome.service';
import { ViewHeaderService } from '../../services/view-header.service';
import { openSidenav } from '../../store/config/config.actions';
import { ButtonComponent } from '../button/button.component';
import { JobActionsDropdownComponent } from '../job-actions-dropdown/job-actions-dropdown.component';

@Component({
  selector: 'app-view-header',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslocoPipe,
    RouterLink,
    FaIconComponent,
    JobActionsDropdownComponent,
    CampaignBookingModalComponent,
    ButtonComponent,
  ],
  template: `
    <div class="view-header-inner">
      @if (isSmallScreen()) {
        <app-button
          variant="ghost"
          [iconStart]="fontawesome.light.bars"
          [ariaLabel]="'company-tool.sidenav.open-menu' | transloco"
          (click)="openSidenav()"
        />
      }
      <div class="view-header-content">
        <h3>{{ lastBreadcrumb()?.label | transloco }}</h3>
        <ul class="view-header-list">
          @for (breadcrumb of visibleBreadcrumbs(); track breadcrumb.label) {
            <li>
              @if (breadcrumb.isLoading) {
                <span aria-hidden="true">
                  <fa-icon
                    [icon]="fontawesome.light.rotate"
                    [spin]="true"
                    size="sm"
                  ></fa-icon>
                </span>
              } @else {
                <a
                  [routerLink]="breadcrumb.routerLink"
                  [class.clickable]="
                    !!breadcrumb.routerLink && breadcrumb.routerLink !== ''
                  "
                  >{{ breadcrumb.label | transloco }}</a
                >
              }
              @switch (breadcrumb.type) {
                @case (BreadcrumbType.JOB) {
                  @if (breadcrumb.jobId !== initiativeId()) {
                    <app-job-actions-dropdown
                      [jobId]="breadcrumb.jobId"
                      alignment="left"
                      (click)="$event.stopPropagation()"
                      (bookDistribution)="openCampaignBooking($event)"
                    ></app-job-actions-dropdown>
                  }
                }
              }
              @if ($index < visibleBreadcrumbs().length - 1) {
                <fa-icon
                  [icon]="fontawesome.light.chevronRight"
                  size="sm"
                ></fa-icon>
              }
            </li>
          }
        </ul>
      </div>
    </div>
    <app-campaign-booking-modal
      #campaignBookingModal
    ></app-campaign-booking-modal>
  `,
  styleUrl: './view-header.component.scss',
})
export class ViewHeaderComponent {
  private readonly store = inject(Store<AppState>);
  private readonly screenService = inject(ScreenService);
  private readonly jobsService = inject(JobsService);
  private readonly campaignBookingModal =
    viewChild<CampaignBookingModalComponent>('campaignBookingModal');

  readonly viewHeader = inject(ViewHeaderService);
  readonly fontawesome = inject(FontawesomeService);
  readonly BreadcrumbType = BreadcrumbType;

  readonly isSmallScreen = toSignal(this.screenService.isSmallScreen$, {
    initialValue: this.screenService.isSmallScreen,
  });
  readonly initiativeId = toSignal(this.jobsService.initiativeID$, {
    initialValue: this.jobsService.initiativeID,
  });
  readonly visibleBreadcrumbs = computed(() =>
    this.viewHeader.breadcrumbs().filter((breadcrumb) => {
      if (breadcrumb.isLoading === true) return true;

      // Keep the job breadcrumb visible even if label is still loading,
      // so the actions dropdown is available immediately after navigation.
      if (breadcrumb.type === BreadcrumbType.JOB && !!breadcrumb.jobId)
        return true;

      return breadcrumb.label.trim() !== '';
    }),
  );

  readonly lastBreadcrumb = computed(() =>
    this.viewHeader.breadcrumbs().at(-1),
  );

  openSidenav(): void {
    this.store.dispatch(openSidenav());
  }

  /**
   * Opens the shared campaign booking modal for the job from the breadcrumb actions.
   * @param {Job} job Job to book distribution for
   * @returns {void}
   */
  openCampaignBooking(job: Job): void {
    this.campaignBookingModal()?.open(job);
  }
}
